import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { isAfter, isBefore, startOfDay } from 'date-fns';
import type AbilitiesService from 'ember-can/services/abilities';
import { task } from 'ember-concurrency';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { and, eq, not } from 'ember-truth-helpers';
import { sourceToAbilityMap } from 'garaje/abilities/entry-approval';
import type { GetCanReviewOrReadBlocklistMatchModel } from 'garaje/abilities/security';
import ScreenerWrapper from 'garaje/components/approvals/screeners/screener-wrapper';
import ApprovalIsExpired from 'garaje/components/approvals/states/approval-is-expired';
import ApprovalIsPropertyBlocklist from 'garaje/components/approvals/states/approval-is-property-blocklist';
import ApprovalNeedsReview from 'garaje/components/approvals/states/approval-needs-review';
import ApprovalWasDenied from 'garaje/components/approvals/states/approval-was-denied';
import VisitApprovalResultResolution from 'garaje/components/approvals/visit-approval-result-resolution';
import UiButton from 'garaje/components/ui-button';
import type { ReportSource } from 'garaje/models/approval-status';
import Entry from 'garaje/models/entry';
import Invite from 'garaje/models/invite';
import type RecurringInviteModel from 'garaje/models/recurring-invite';
import RecurringRule from 'garaje/models/recurring-rule';
import type ScreenerResult from 'garaje/models/screener-result';
import type User from 'garaje/models/user';
import type VisitApproval from 'garaje/models/visit-approval';
import type { GuestWithVisitApproval } from 'garaje/models/visit-approval';
import type AjaxService from 'garaje/services/ajax';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LoggerService from 'garaje/services/logger';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import type { WorkflowTasksPermissionsResponse } from 'garaje/types/workflow-approvals';
import { approvalMessage } from 'garaje/utils/approval-message';
import { deriveCanApproveWorkflow } from 'garaje/utils/derive-can-approve-workflow';
import { entryApprovalMessage } from 'garaje/utils/entry-approval-message';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { canTriggerWorkflowReeval } from 'garaje/utils/invite-workflow-reeval';
import { refreshApprovalChainAuditLogs } from 'garaje/utils/refresh-approval-chain-audit-logs';
import uniq from 'lodash/uniq';
import { reads } from 'macro-decorators';

export interface VisitApprovalResultTilesSignature {
  Args: {
    model: GuestWithVisitApproval;
    blocklistContacts: User[];
    idScanContacts: User[];
    onStatusChange?: () => void;
  };
  Element: HTMLDivElement;
}

export default class VisitApprovalResultTiles extends Component<VisitApprovalResultTilesSignature> {
  @service declare abilities: AbilitiesService;
  @service declare ajax: AjaxService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare logger: LoggerService;
  @service declare metrics: MetricsService;
  @service declare state: StateService;

  @tracked workflowPermissionsChecked = false;
  @tracked canApproveWorkflow = false;

  constructor(owner: unknown, args: VisitApprovalResultTilesSignature['Args']) {
    super(owner, args);

    if (this.featureFlags.isEnabled('ava-feature') && this.workflowId) {
      void this.fetchWorkflowPermissionsTask.perform();
    } else {
      this.workflowPermissionsChecked = true;
    }
  }

  /**
   * Returns the workflow ID if a visit-workflows screener result exists with a workflowId.
   */
  get workflowId(): string | null {
    if (!this.featureFlags.isEnabled('ava-feature')) {
      return null;
    }

    const resultWithWorkflow = this.args.model.visitApproval.screenerResults.find(
      (result) => result.workflowId !== null,
    );
    return resultWithWorkflow?.workflowId ?? null;
  }

  get reviewable(): VisitApproval['reviewableScreenerSources'] {
    return this.args.model.visitApproval.reviewableScreenerSources;
  }

  get inReview(): VisitApproval['reviewResults'] {
    return this.args.model.visitApproval.reviewResults;
  }

  /**
   * Returns true if there's a visit-workflow screener in review state.
   */
  get hasWorkflowInReview(): boolean {
    return this.args.model.visitApproval.visitWorkflowReviewResult !== undefined;
  }

  /**
   * The count of screeners the user can actually review, accounting for workflow permissions.
   * Excludes visit-workflows unless the user can approve the current workflow step.
   */
  get effectiveReviewableCount(): number {
    const baseCount = this.args.model.visitApproval.reviewableScreenerSourcesWithoutWorkflow.length;
    // Add 1 for workflow only if there's a workflow in review AND user can approve it
    if (this.hasWorkflowInReview && this.canApproveWorkflow) {
      return baseCount + 1;
    }
    return baseCount;
  }

  /**
   * The count of screeners in review, accounting for workflow permissions.
   * Excludes visit-workflows unless the user can approve the current workflow step.
   */
  get effectiveInReviewCount(): number {
    const baseCount = this.inReview.filter((result) => result.screenerSource !== 'visit-workflows').length;
    // Add 1 for workflow only if there's a workflow in review AND user can approve it
    if (this.hasWorkflowInReview && this.canApproveWorkflow) {
      return baseCount + 1;
    }
    return baseCount;
  }

  @reads('state.features.canAccessMultiPartApprovals') canAccessMultiPartApprovals!: boolean;

  get isExpired(): boolean {
    return Boolean(this.pendingReview && this.isBeforeToday && this.hasNoFutureOccurences);
  }

  get date(): Date | undefined {
    return this.invite?.expectedArrivalTime || this.entry?.signedInAt;
  }

  get pendingReview(): boolean {
    return this.args.model.needsApprovalReview;
  }

  get hasNoFutureOccurences(): boolean {
    return !this.isRecurringInviteWithFutureOccurrences;
  }

  get recurringRule(): RecurringInviteModel['recurringRule'] | undefined {
    const recurringInvite = this.invite?.belongsTo('recurringInvite').value() as RecurringInviteModel | null;
    return recurringInvite?.recurringRule;
  }

  get wasDenied(): boolean {
    return this.args.model.approvalWasDenied;
  }

  /**
   * Hosts/creators/assistants can resubmit a denied invite to trigger workflow re-evaluation
   * when the invite went through a workflow.
   */
  get canResubmit(): boolean {
    if (!this.invite) return false;
    if (!canTriggerWorkflowReeval(this.invite)) return false;
    return this.abilities.can('edit invite', this.invite);
  }

  get fullName(): string {
    return this.args.model.fullName;
  }

  get canReviewOrRead(): boolean {
    // for multi part approvals, we should still be able to review or read parts of the approval that we have access to
    return this.canReviewSomeScreeners || this.canViewSomeScreeners;
  }

  /**
   * user can review at least one screener regardless of the review status
   */
  get canReviewSomeScreeners(): boolean {
    return this.abilities.can('review some entry-approvals', this.#permissionsModel);
  }

  /**
   * user can view at least one screener regardless of the review status
   */
  get canViewSomeScreeners(): boolean {
    return this.abilities.can('view some entry-approvals', this.#permissionsModel);
  }

  get isBeforeToday(): boolean {
    return this.date ? isBefore(this.date, startOfDay(new Date())) : false;
  }

  get isRecurringInviteWithFutureOccurrences(): boolean {
    if (!this.recurringRule) return false;
    const { until } = RecurringRule.parse(this.recurringRule);
    return !until || isAfter(until, new Date());
  }

  get entry(): Entry | undefined {
    return this.args.model instanceof Entry ? this.args.model : undefined;
  }

  get invite(): Invite | undefined {
    return this.args.model instanceof Invite ? this.args.model : undefined;
  }

  get contactedNames(): string[] {
    let contacts: string[] = [];
    if (this.args.model.visitApproval.blocklistReviewResult && this.args.blocklistContacts) {
      contacts = [...contacts, ...this.args.blocklistContacts.map((contact) => contact.fullName)];
    }
    if (
      this.args.model.visitApproval.idScanningReviewResult &&
      this.args.idScanContacts.map((contact) => contact.fullName)
    ) {
      contacts = [...contacts, ...this.args.idScanContacts.map((contact) => contact.fullName)];
    }
    return uniq(contacts);
  }

  get reviewerName(): string | undefined {
    // a single deny review is sufficient to deny the entire approval. So we only need to check for the first denied review
    return this.args.model.visitApproval.screenerReviews.filter((review) => review.isDenied)[0]?.reviewerName;
  }

  get disabledReviewButtons(): boolean {
    return Boolean(
      this.approveTask.isRunning ||
        this.denyTask.isRunning ||
        this.args.model.visitApproval.idScanningPendingResult ||
        !this.workflowPermissionsChecked,
    );
  }

  /**
   * Checks if the current user has permission to approve the current workflow step.
   */
  fetchWorkflowPermissionsTask = task(async (): Promise<void> => {
    if (!this.workflowId) {
      this.workflowPermissionsChecked = true;
      return;
    }

    try {
      const response = await this.ajax.request<WorkflowTasksPermissionsResponse>(
        `/a/visits/api/v1/workflows/${this.workflowId}/tasks/permissions`,
      );

      this.canApproveWorkflow = deriveCanApproveWorkflow(response);
    } catch (_error) {
      // Any error (404, 500, etc.) should disable workflow approval
      this.canApproveWorkflow = false;
    } finally {
      this.workflowPermissionsChecked = true;
    }
  });

  /**
   * Notifies the workflow system of an approval/denial decision.
   */
  async notifyWorkflow(workflowId: string, approved: boolean): Promise<void> {
    if (!this.featureFlags.isEnabled('ava-feature')) {
      return;
    }

    try {
      await this.ajax.request(`/a/visits/api/v1/workflows/${workflowId}/approvals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          approved,
          reason: null,
        },
      });
    } catch (error) {
      this.flashMessages.showAndHideFlash('error', 'Error notifying workflow system', {
        details: parseErrorForDisplay(error),
      });
      throw error; // Re-throw to prevent showing success message
    }
  }

  approveTask = task(
    waitFor(async (screeners?: ReportSource[]) => {
      try {
        const { model } = this.args;
        const { workflowId } = this;
        const isBulkApprove = screeners === undefined;
        const screenersToApprove = screeners ?? this.args.model.visitApproval.reviewableScreenerSourcesWithoutWorkflow;
        const shouldNotifyWorkflow = isBulkApprove && workflowId && this.canApproveWorkflow;

        // Handle workflow approval separately if user can approve workflow.
        if (shouldNotifyWorkflow) {
          await this.notifyWorkflow(workflowId, true);
        }

        // Approve other screeners (excluding visit-workflows)
        if (screenersToApprove.length > 0) {
          await model.approve(screenersToApprove);
        }

        // load async relationship for entryApprovalMessage helper dependency
        if (this.entry) {
          const flow = await model.flow;
          await flow?.badge;
        }

        let additionalFlashMessageText: string | undefined;

        if (this.invite && !this.invite.preregistrationComplete && this.invite.approved) {
          additionalFlashMessageText = 'Invitation sent!';
        } else if (this.entry) {
          additionalFlashMessageText = entryApprovalMessage(this.entry);
        }

        additionalFlashMessageText = approvalMessage(this.args.model) ?? additionalFlashMessageText;

        this.flashMessages.showAndHideFlash('success', 'Access approved', { details: additionalFlashMessageText });
        this.trackReviewMetrics('approve', screenersToApprove);
        this.args.onStatusChange?.();
        await refreshApprovalChainAuditLogs(this.logger);

        if (shouldNotifyWorkflow) {
          window.location.reload();
        }
      } catch (error) {
        this.flashMessages.showAndHideFlash('error', `Error approving ${this.entry ? 'entry' : 'invite'}`, {
          details: parseErrorForDisplay(error),
        });
      }
    }),
  );

  denyTask = task(
    waitFor(async () => {
      try {
        // Handle workflow denial separately if user can approve/deny workflow
        if (this.workflowId && this.canApproveWorkflow) {
          await this.notifyWorkflow(this.workflowId, false);
        }

        // Deny the invite/entry (this denies all non-workflow screeners)
        await this.args.model.deny();
        this.flashMessages.showAndHideFlash('warning', `${this.entry ? 'Entry' : 'Invite'} denied`);

        this.trackReviewMetrics(
          'deny',
          this.args.model.visitApproval.screenerResults.map((result) => result.screenerSource),
        );
        this.args.onStatusChange?.();
        await refreshApprovalChainAuditLogs(this.logger);

        if (this.workflowId && this.canApproveWorkflow) {
          window.location.reload();
        }
      } catch (error) {
        this.flashMessages.showAndHideFlash('error', `Error denying ${this.entry ? 'entry' : 'invite'}`, {
          details: parseErrorForDisplay(error),
        });
      }
    }),
  );

  trackReviewMetrics(action: string, sources: ReportSource[] = []): void {
    this.metrics.trackEvent(`Dashboard ${this.entry ? 'Entry' : 'Invite'} - Reviewed`, {
      action,
      [this.entry ? 'entry_id' : 'invite_id']: this.args.model.id,
      source: `${this.entry ? 'Entry' : 'Invite'} Details`,
      ...this.getSourcesForApprovalReviewMetrics(sources),
    });
  }

  getSourcesForApprovalReviewMetrics(sources: ReportSource[]): Record<string, boolean> {
    return sources.reduce((acc, report) => ({ ...acc, [report]: true }), {}) ?? {};
  }

  get #permissionsModel(): GetCanReviewOrReadBlocklistMatchModel {
    return {
      context: 'location',
      parent: this.args.model,
      visitApproval: this.args.model.visitApproval,
    };
  }

  get visibleTiles(): ScreenerResult[] {
    return this.args.model.visitApproval.reviewResults.filter((report) => {
      const { screenerSource } = report;

      if (screenerSource === 'visit-workflows') {
        return false;
      }

      const source = sourceToAbilityMap[screenerSource] || screenerSource;

      const canReview = this.abilities.can(`review ${source}`, this.#permissionsModel);
      const canView = this.abilities.can(`view ${source}`, this.#permissionsModel);

      return canReview || canView;
    });
  }

  get showRecurringTooltip(): boolean {
    return this.effectiveReviewableCount === this.effectiveInReviewCount && this.isRecurringInviteWithFutureOccurrences;
  }

  get allReviewable(): boolean {
    return this.effectiveReviewableCount === this.effectiveInReviewCount;
  }

  get isPropertyBlocklist(): boolean {
    return this.args.model.visitApproval.screenerResults.slice().some((result) => result.isPropertyBlocklist);
  }

  get canAccessReviewButtons(): boolean {
    if (this.canAccessMultiPartApprovals) {
      // if the multi part feature is enabled, we can always show the buttons if there
      // is more than one reviewable screener
      return this.effectiveReviewableCount > 1;
    } else {
      // if the multi part feature is not enabled, we can only show
      // the buttons if they can review all reviewable screener results
      return this.effectiveReviewableCount > 1 && this.allReviewable;
    }
  }

  <template>
    <div data-test-visit-approval-result-tiles ...attributes>
      {{#if (and this.pendingReview (not this.isExpired))}}
        {{#if this.canReviewOrRead}}
          {{! invite or entry is active and needs review }}
          {{#if this.canAccessReviewButtons}}
            <h3 class="label mt-1 mb-4">Review and approve</h3>

            <strong class="text-sm" data-test-reviewable-message>
              {{this.effectiveReviewableCount}}
              of
              {{this.effectiveInReviewCount}}
              approvals are assigned to you.
            </strong>
            <div class="flex gap-x-4 mt-1 mb-6">
              <UiButton
                class="btn btn--small btn--outline flex-1"
                data-test-approve-all
                disabled={{this.disabledReviewButtons}}
                {{on "click" (fn this.approveTask.perform undefined)}}
              >
                {{svgJar "checkmark-green" height="12" width="12" class="flex-none mr-2 align-middle"}}
                Approve
                {{#if this.allReviewable}}All{{/if}}
              </UiButton>
              <UiButton
                class="btn btn--small btn--outline flex-1"
                data-test-deny-all
                disabled={{this.disabledReviewButtons}}
                {{on "click" this.denyTask.perform}}
              >
                {{svgJar "cross-small-red" height="12" width="12" class="flex-none mr-2 align-middle"}}
                Deny All
              </UiButton>
              {{#if @model.visitApproval.idScanningPendingResult}}
                <EmberTooltip @side="top" @text="Please wait until the visitor's ID is done processing." />
              {{/if}}
            </div>
          {{/if}}

          {{#if this.showRecurringTooltip}}
            <EmberTooltip
              @side="top"
              @tooltipClass="!rounded-none !p-2 !border !border-carbon-20 !text-carbon-80 !bg-white text-shadow-none z-50 !max-w-xs [&_.ember-tooltip-arrow]:hidden"
            >
              <strong>Future invites will be auto-approved</strong>
              <p class="mb-0" data-test-recurring-invite-info>
                Approving all checks on this invite will also approve future invites for this visitor.
              </p>
            </EmberTooltip>
          {{/if}}

          {{#each this.visibleTiles as |result|}}
            <ScreenerWrapper
              @model={{@model}}
              @result={{result}}
              @approveTask={{this.approveTask}}
              @denyTask={{this.denyTask}}
              @showDenyButton={{if (eq this.effectiveReviewableCount 1) true}}
            />
          {{/each}}
        {{else}}
          {{! invite or entry is active and needs review but user cannot view or review anything }}
          <ApprovalNeedsReview @entry={{this.entry}} @invite={{this.invite}} @contactedNames={{this.contactedNames}} />
        {{/if}}

        {{#if (and this.isPropertyBlocklist this.invite)}}
          <ApprovalIsPropertyBlocklist />
        {{/if}}
      {{else}}
        {{#if (and (not this.canReviewOrRead) this.wasDenied)}}
          {{! invite or entry is denied and user cannot review or read anything }}
          <ApprovalWasDenied
            @entry={{this.entry}}
            @invite={{this.invite}}
            @fullName={{this.fullName}}
            @reviewerName={{this.reviewerName}}
            @canResubmit={{this.canResubmit}}
          />
        {{else if this.isExpired}}
          {{! invite or entry is expired and user cannot review or read anything }}
          <ApprovalIsExpired @entry={{this.entry}} @invite={{this.invite}} />
        {{/if}}
      {{/if}}
    </div>

    <VisitApprovalResultResolution @model={{@model}} @isExpired={{this.isExpired}} />
  </template>
}
